import {
    Box,
    Button,
    Card,
    CircularProgress,
    Snackbar,
    Typography,
  } from '@mui/material';
  import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
  } from 'react';
  import { useNavigate } from 'react-router-dom';
  import api from '../../../API/api';
  import { ConfigContext } from '../../App/App';
  import { AppContext } from '../../App/AppContext';
  import styles from './DynamicForm.module.css';
  import DynamicFormField from './DynamicFormFields/DynamicFormField';
  
  /* ---------- types ---------- */
  export interface FormField {
    Label: string;
    Description: string;
    DataType: string;
    AllowedValues: string[];
    Required: boolean;
    FieldId: string;
    Validation: string;
    Children: FormField[];
    ChildrenShowOnValue: string;
  }
  
  /* ---------- helpers ---------- */
  const isEmpty = (v: any) =>
    v === null ||
    v === undefined ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0);
  
  const findFieldByLabel = (fields: FormField[], label: string) => {
    for (const f of fields) {
      if (f.Label === label) return f;
      const child = f.Children.find((c) => c.Label === label);
      if (child) return child;
    }
    return undefined;
  };
  
  function DynamicForm() {
    const config = useContext(ConfigContext);
    const { dispatch, state } = useContext(AppContext);
    const navigate = useNavigate();
  
    /* ----------- derive form fields from config ----------- */
    const formFields: FormField[] = useMemo(() => {
      const props = config?.EntranceForm?.form_details?.properties;
      if (!props || Object.keys(props).length === 0) return [];
  
      return Object.entries(props).map(([, first]) => {
        const meta = (first as any).metadata?.[0] ?? {};
        const rawChildren = (first as any).children ?? null;
  
        const children: FormField[] = rawChildren
          ? Object.entries(rawChildren).map(([, ch]) => {
              const meta2 = (ch as any).metadata?.[0] ?? {};
              return {
                Label: (ch as any).title,
                Description: (ch as any).description,
                DataType: meta2.data_type,
                Required: meta2.required,
                AllowedValues: meta2.allowed_values,
                Validation: meta2.validation,
                FieldId: meta2.field_id,
                Children: [],
                ChildrenShowOnValue: '',
              };
            })
          : [];
  
        return {
          Label: (first as any).title,
          Description: (first as any).description,
          DataType: meta.data_type,
          Required: meta.required,
          AllowedValues: meta.allowed_values,
          Validation: meta.validation,
          FieldId: meta.field_id,
          Children: children,
          ChildrenShowOnValue: (first as any).children_show_on_value ?? '',
        };
      });
    }, [config]);
  
    /* -------- initial validation skeleton -------- */
    const buildInitialValidations = useCallback(
      () =>
        formFields.flatMap((f) =>
          f.Children.length
            ? [
                {
                  FieldName: f.Label,
                  IsValid: !f.Required,
                  Error: '',
                  FieldId: f.FieldId,
                  Children: f.Children.map((c) => c.FieldId),
                },
                ...f.Children.map((c) => ({
                  FieldName: c.Label,
                  IsValid: !c.Required,
                  Error: '',
                  FieldId: c.FieldId,
                })),
              ]
            : [
                {
                  FieldName: f.Label,
                  IsValid: !f.Required,
                  Error: '',
                  FieldId: f.FieldId,
                },
              ]
        ),
      [formFields],
    );
  
    const [fieldValidations, setFieldValidations] = useState<any[]>(
      buildInitialValidations
    );
  
    const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
    if(fieldValues){
        
    }
  
    const [formValid, setFormValid] = useState(() =>
      fieldValidations.every((v) => v.IsValid)
    );
  
    /* rebuild skeleton if formFields change */
    useEffect(() => {
      setFieldValidations(buildInitialValidations());
      setFormValid(formFields.every((f) => !f.Required));
    }, [formFields, buildInitialValidations]);
  
    /* ---------- stable validation callback ---------- */
    const validateField = useCallback(
      (fieldName: string, isValid: boolean, error: string, fieldValue: any) => {
        setFieldValues((currentValues) => {
          const newFieldValues = { ...currentValues, [fieldName]: fieldValue };
  
          setFieldValidations((prevValidations) => {
            let next = [...prevValidations];
            const metaField = findFieldByLabel(formFields, fieldName);
  
            // 1) If this field has children that appear/disappear based on value
            if (metaField && metaField.Children.length) {
              const showing = metaField.ChildrenShowOnValue === fieldValue;
              if (showing) {
                metaField.Children.forEach((c) => {
                  const idx = next.findIndex((v) => v.FieldName === c.Label);
                  const rec = {
                    FieldName: c.Label,
                    IsValid: !c.Required,
                    Error: '',
                    FieldId: c.FieldId,
                  };
                  idx === -1 ? next.push(rec) : (next[idx] = rec);
                });
              } else {
                next = next.filter(
                  (v) => !metaField.Children.some((c) => c.FieldId === v.FieldId)
                );
              }
            }
  
            // 2) If we're a child of another field that isn't showing, skip
            const parentOf = formFields.find((p) =>
              p.Children.some((c) => c.Label === fieldName)
            );
            if (
              parentOf &&
              fieldName !== parentOf.Label &&
              newFieldValues[parentOf.Label] !== parentOf.ChildrenShowOnValue
            ) {
              setFormValid(next.every((v) => v.IsValid));
              return next;
            }
  
            // 3) Required check — no error text for required empties
            if (metaField?.Required && isEmpty(fieldValue)) {
              isValid = false;
              // We do NOT set error = 'This field is required.'
            }
  
            // 4) Upsert
            const idx = next.findIndex((v) => v.FieldName === fieldName);
            const updated = {
              FieldName: fieldName,
              IsValid: isValid,
              Error: error,
              FieldId: metaField?.FieldId ?? '',
              FieldValue: fieldValue,
            };
            idx === -1 ? next.push(updated) : (next[idx] = updated);
  
            // 5) final form validity
            setFormValid(next.every((v) => v.IsValid));
            return next;
          });
  
          return newFieldValues;
        });
      },
      [formFields]
    );
  
    /* ---------- submit ---------- */
    const [submitting, setSubmitting] = useState(false);
    const [snackOpen, setSnackOpen] = useState(false);
  
    const afterSubmit = () => {
      dispatch({ type: 'setEnteredInformation', hasEnteredInformation: true });
      navigate('/Home');
    };
  
    const submitForm = async () => {
      if (!formValid) return;
      setSubmitting(true);
  
      const request = {
        indexname: config.IndexName,
        sessionid: state.sessionid,
        objectid:
          config?.EntranceForm?.form_details?.object_id ?? 'missing-object-id',
        fields: fieldValidations.map((v) => ({
          fieldid: v.FieldId,
          fieldvalue: v.FieldValue,
        })),
      };
  
      try {
        await api.submitDyanmicInformation.submitDyanmicInformation(request);
        afterSubmit();
      } catch (err) {
        console.error(err);
        setSnackOpen(true);
      } finally {
        setSubmitting(false);
      }
    };
  
    /* ---------- auto-skip if no fields ---------- */
    useEffect(() => {
      if (formFields.length === 0) {
        dispatch({ type: 'setEnteredInformation', hasEnteredInformation: true });
        navigate('/Home', { replace: true });
      }
    }, [formFields.length, dispatch, navigate]);
  
    if (formFields.length === 0) return null;
  
    return (
      <Box component="main" id="main" sx={{ flex: 1 }}>
        <Card
          sx={{
            p: 3,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            justifyContent: 'space-between',
          }}
        >
          {submitting && (
            <Card
              role="status"
              aria-live="polite"
              sx={{
                background: 'white',
                opacity: 0.5,
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 99,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress />
            </Card>
          )}
  
          <form className="welcomeSubmit" aria-busy={submitting}>
            <Box sx={{ mb: 2 }}>
              <Typography
                component="h1"
                sx={{ fontSize: '1.5rem', fontWeight: 'bold', textAlign: 'center' }}
              >
                Welcome!
              </Typography>
              <Typography sx={{ textAlign: 'center' }}>
                Enter your information to get started.
              </Typography>
            </Box>
  
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {formFields.map((ff) => (
                <DynamicFormField
                  key={ff.FieldId}
                  form_field={ff}
                  validateField={validateField}
                  isChild={false}
                />
              ))}
            </Box>
  
            <Button
              variant="outlined"
              type="button"
              onClick={submitForm}
              disabled={!formValid || submitting}
              sx={{
                width: '100%',
                mt: 2,
                background: formValid
                  ? `${config.PrimaryColor} !important`
                  : '#e8e8e8 !important',
                color: formValid ? 'white !important' : 'gray !important',
                border: '0 !important',
              }}
              className={styles.submitDynamicFormButton}
            >
              Get&nbsp;Started
            </Button>
          </form>
        </Card>
  
        <Snackbar
          open={snackOpen}
          autoHideDuration={4000}
          onClose={() => setSnackOpen(false)}
          message="Something went wrong. Please try again."
        />
      </Box>
    );
  }
  
  export default DynamicForm;
  